/**
 * CalendarEventModalRouter
 *
 * Roteia o clique em um evento do Calendário Global para o modal de detalhes
 * correspondente à fonte (source_type):
 *   - manual   → ManualEventModal (edição) ou CalendarEventInfoDialog (sem permissão)
 *   - task     → TaskDetailsModal
 *   - service_order → ServiceOrderDetailsModal
 *   - activity → DealDetailsModalPremium (resolve deal_id da atividade e
 *                abre o modal completo do negócio, mesmo comportamento da
 *                listagem de Atividades)
 *   - booking  → CalendarEventInfoDialog (read-only)
 *   - google   → CalendarEventInfoDialog (read-only, com link Meet)
 *
 * Mantém o Calendar.tsx enxuto e permite adicionar novas fontes sem alterar
 * o fluxo principal.
 *
 * RELEASE:
 *   2026-10-09 v1.3.0 - Abre a ficha oficial da Ordem de Serviço.
 *   2026-06-25 v1.2.0 - activity + booking passam a abrir CalendarEventDetailsModal
 *                       (mesmo modal da Agenda CRM, com Concluir/Reabrir/Cancelar/
 *                       Ver Negócio). Mantém risco visual quando concluído/cancelado.
 *   2026-06-24 v1.1.0 - Branch 'activity' passa a abrir DealDetailsModalPremium.
 *   2026-06-24 v1.0.0 - Versão inicial
 */

import { useEffect, useState } from 'react';
import type { UnifiedCalendarEvent } from '@/hooks/useCalendarUnifiedEvents';
import type { UserCalendar } from '@/hooks/useCalendars';
import { ManualEventModal } from './ManualEventModal';
import { CalendarEventInfoDialog } from './CalendarEventInfoDialog';
import { CalendarEventDetailsModal } from './CalendarEventDetailsModal';
import { TaskDetailsModal } from '@/components/tasks/modal/TaskDetailsModal';
import { ScheduleMessageDialog } from '@/components/tenant/chat/ScheduleMessageDialog';
import { toast } from 'sonner';
import { ServiceOrderDetailsModal } from '@/components/service-orders/ServiceOrderDetailsModal';

/**
 * Metadados injetados em attendees pelo RPC get_calendar_unified_events
 * quando source_type = 'scheduled_message'.
 */
interface ScheduledMessageAttendees {
  conversation_id?: string;
  inbox_id?: string;
  channel_type?: string;
  message_payload?: any;
  cancel_on_reply?: boolean;
  internal_note?: string;
  linked_macro_rule_id?: string | null;
  linked_macro_execution_mode?: string | null;
}

interface CalendarEventModalRouterProps {
  /** Evento selecionado (null = nenhum modal aberto). */
  event: UnifiedCalendarEvent | null;
  /** Calendários disponíveis (necessário para ManualEventModal). */
  calendars: UserCalendar[];
  /** Fecha o modal ativo. */
  onClose: () => void;
}

export function CalendarEventModalRouter({
  event,
  calendars,
  onClose,
}: CalendarEventModalRouterProps) {
  const open = !!event;

  // Controle interno do dialog de fallback (info read-only) para 'manual'
  // sem permissão de edição. Activity/booking agora usam CalendarEventDetailsModal.
  const [infoFallback, setInfoFallback] = useState(false);
  useEffect(() => {
    if (!event) {
      setInfoFallback(false);
      return;
    }
    if (
      event.source_type === 'manual' &&
      event.user_permission !== 'edit' &&
      event.user_permission !== 'manage'
    ) {
      setInfoFallback(true);
    } else {
      setInfoFallback(false);
    }
  }, [event]);

  if (!event) return null;

  // ----- MANUAL: edição (ou info se sem permissão) ------------------------
  if (event.source_type === 'manual' && !infoFallback) {
    return (
      <ManualEventModal
        open={open}
        onOpenChange={(o) => !o && onClose()}
        calendars={calendars}
        event={event}
      />
    );
  }

  // ----- TASK -------------------------------------------------------------
  if (event.source_type === 'task') {
    return (
      <TaskDetailsModal
        taskId={event.source_id}
        open={open}
        onOpenChange={(o) => !o && onClose()}
      />
    );
  }

  // ----- ORDEM DE SERVIÇO -------------------------------------------------
  if (event.source_type === 'service_order') {
    return (
      <ServiceOrderDetailsModal
        serviceOrderId={event.source_id}
        open={open}
        onOpenChange={(o) => !o && onClose()}
      />
    );
  }

  // ----- ACTIVITY / BOOKING ----------------------------------------------
  // v1.2.0: ambos abrem o CalendarEventDetailsModal (mesmo modal da Agenda
  // CRM). Permite Concluir/Reabrir/Cancelar/Ver Negócio direto no calendário.
  if (event.source_type === 'activity' || event.source_type === 'booking') {
    return (
      <CalendarEventDetailsModal
        event={event}
        open={open}
        onClose={onClose}
      />
    );
  }

  // ----- SCHEDULED MESSAGE -----------------------------------------------
  if (event.source_type === 'scheduled_message') {
    const meta = (event.attendees ?? {}) as ScheduledMessageAttendees;
    const payload = meta.message_payload ?? {};
    return (
      <ScheduleMessageDialog
        open={open}
        onOpenChange={(o) => !o && onClose()}
        scheduledId={event.source_id ?? undefined}
        conversationId={meta.conversation_id}
        conversationContext={
          meta.inbox_id || meta.channel_type
            ? {
                inboxId: meta.inbox_id,
                channelType: meta.channel_type,
              }
            : undefined
        }
        initial={{
          scheduledAt: event.start_at,
          text: typeof payload?.text === 'string' ? payload.text : undefined,
          cancelOnReply: meta.cancel_on_reply ?? true,
          internalNote: meta.internal_note ?? undefined,
          linkedMacroRuleId: meta.linked_macro_rule_id ?? null,
          linkedMacroMode:
            (meta.linked_macro_execution_mode as
              | 'after_message'
              | 'before_message'
              | 'replace_message'
              | null) ?? null,
          messagePayload: payload,
        }}
        readOnly={event.user_permission !== 'edit' && event.user_permission !== 'manage'}
      />
    );
  }

  // ----- GOOGLE / MANUAL (fallback read-only) ----------------------------
  return (
    <CalendarEventInfoDialog
      event={event}
      open={open}
      onOpenChange={(o) => !o && onClose()}
    />
  );
}
